/**
 * Utility functions for building Snyk URLs
 */

export interface SnykUrlParams {
  'context[page]': string;
  issue_status: string;
  issue_by: string;
  table_issues_detail_cols: string;
  tableRowsPerPage: string;
  table_issues_detail_sort: string;
  project_target?: string;
  project_origin?: string;
}

export interface SnykUrlBuilderOptions {
  snykAppHost: string;
  orgName: string;
  snykTargets?: string;
}

export interface SnykUrlBuilderWithProjectIDOptions {
  snykAppHost: string;
  orgName: string;
  projectIds: string | undefined;
}

/**
 * Checks if a value is a valid non-empty string
 */
export const isValidString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Determines if a parameter should be double-encoded
 */
const shouldDoubleEncode = (key: string): boolean =>
  [
    'project_target',
    'project_origin',
    'issue_status',
    'table_issues_detail_cols',
    'table_issues_detail_sort',
  ].includes(key);

/**
 * Encodes a URL parameter based on the parameter type
 */
const encodeUrlParameter = (key: string, value: string): string => {
  if (key === 'context[page]') {
    // Don't encode the brackets in context[page]
    return `${key}=${encodeURIComponent(value)}`;
  }

  if (shouldDoubleEncode(key)) {
    // Double encode for specific parameters
    return `${encodeURIComponent(key)}=${encodeURIComponent(
      encodeURIComponent(value),
    )}`;
  }

  // Single encode for other parameters
  return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
};

/**
 * Parses comma-separated targets and returns array of target names
 */
const parseTargets = (targets: string): string[] => {
  return targets
    .split(',')
    .map(t => t.trim())
    .filter(t => t.length > 0);
};

/**
 * Builds a Snyk reporting URL with proper encoding
 * Supports multiple comma-separated targets
 */
export const buildSnykUrl = (
  options: SnykUrlBuilderOptions,
): string | undefined => {
  const { snykAppHost, orgName, snykTargets } = options;

  if (!isValidString(orgName)) {
    return undefined;
  }

  const baseReportingUrl = `https://${snykAppHost}/org/${orgName}/reporting`;

  // Create base parameters
  const params: SnykUrlParams = {
    'context[page]': 'issues-detail',
    issue_status: '["Open"]',
    issue_by: 'Severity',
    table_issues_detail_cols:
      'SEVERITY|SCORE|CVE|CWE|PROJECT|EXPLOIT MATURITY|COMPUTED FIXABILITY|INTRODUCED|SNYK PRODUCT',
    tableRowsPerPage: '10',
    table_issues_detail_sort: 'SCORE DESC',
  };

  // Add project-specific filters when available
  if (isValidString(snykTargets)) {
    const targetsList = parseTargets(snykTargets);
    if (targetsList.length > 0) {
      // Build JSON array of targets
      const targetsArray = targetsList.map(target => `"${target}"`).join(',');
      params.project_target = `[${targetsArray}]`;
      params.project_origin = '["github-cloud-app"]';
    }
  }

  // Build query string with proper encoding
  const queryParts = Object.entries(params)
    .filter(([, value]) => isValidString(value))
    .map(([key, value]) => encodeUrlParameter(key, value));

  return `${baseReportingUrl}?${queryParts.join('&')}`;
};

/**
 * Parses comma-separated project IDs and returns the first valid one
 * Snyk project URL only supports single project view
 */
const parseProjectIds = (projectIds: string): string | undefined => {
  const ids = projectIds
    .split(',')
    .map(id => id.trim())
    .filter(id => id.length > 0);

  return ids.length > 0 ? ids[0] : undefined;
};

/**
 * Builds a Snyk project URL
 * When multiple project IDs are provided (comma-separated), uses the first one
 */
export const buildSnykUrlWithProjectId = (
  options: SnykUrlBuilderWithProjectIDOptions,
): string | undefined => {
  const { snykAppHost, orgName, projectIds } = options;

  if (
    !isValidString(snykAppHost) ||
    !isValidString(orgName) ||
    !isValidString(projectIds)
  ) {
    return undefined;
  }

  const projectId = parseProjectIds(projectIds);
  if (!projectId) {
    return undefined;
  }

  return `https://${snykAppHost}/org/${orgName}/project/${projectId}`;
};
